import { MonitoringGoodsDetailDaily } from '@/types/api';
import TrashIcon from '@/assets/icons/Monitoring/Trash_1.svg?react';
import { usePost, CPLAT_API_ROUTES } from '@/api';
import { useAppSelector } from '@/redux/store';
import { useToast } from '@/hooks/V3/useToast';
import { useMonitoringGoods } from './MonitoringContext';

// 예시 props: selectedGoods.MonitoringGoodsDetailDailyList
const GoodsDetailKeywordTable = ({
  dailyList,
  selectedGoods,
  isMockGoods,
}: {
  dailyList: MonitoringGoodsDetailDaily[];
}) => {
  if (!dailyList || dailyList.length === 0) return <div>데이터가 없습니다.</div>;
  const user = useAppSelector((state) => state.user);
  const { refetch } = useMonitoringGoods();
  const { showToast } = useToast();

  // 날짜(컬럼) 추출 (최신순 정렬)
  const dateList = dailyList.map((d) => d.MonitoringDate?.slice(0, 10)).filter(Boolean);

  // 키워드(행) 추출 (첫 번째 날짜 기준)
  const keywordList = dailyList[0]?.RelatedKeywordsRanking?.map((k: any) => k.Keyword) || [];

  // 키워드별 날짜별 데이터 매핑
  const getKeywordRank = (day: any, keyword: string) => {
    const kw = day.RelatedKeywordsRanking.find((k: any) => k.Keyword === keyword);
    if (!kw) return { rank: '-', status: '', range: 0 };
    return {
      rank: kw.Rank > 0 ? `${kw.Rank}위` : '1000위 밖',
      status: kw.RankingChangedStatus,
      range: kw.RankingChangedRange,
    };
  };

  // 키워드 변경 mutation (API 엔드포인트/파라미터는 실제 명세에 맞게)
  const { mutate: updateKeywords, isPending: isUpdating } = usePost(
    CPLAT_API_ROUTES.setUserMonitoringGoods,
  );
  const handleRemoveKeywords = (keyword: string) => {
    if (isMockGoods) {
      showToast('warning', '예시 상품의 키워드는 삭제할 수 없어요');
      return;
    }
    // 키워드 삭제 로직 구현
    updateKeywords(
      {
        cplatToken: user.CplatToken,
        StoreName: selectedGoods.StoreName,
        GoodsNo: selectedGoods.GoodsNo,
        GoodsImageUrl: selectedGoods.GoodsImageUrl,
        GoodsName: selectedGoods.GoodsName,
        CategoryFullName: selectedGoods.CategoryFullName,
        UseYn: 'Y',
        RelatedKeywordList: selectedGoods.RelatedKeyword.filter((k: string) => k !== keyword),
        GoodsUrl: selectedGoods.GoodsUrl,
        GoodsPrice: selectedGoods.GoodsPrice,
        relatedKeywordRankInfo: {}, // 셀러박스에서는 변화량 데이터 빈 객체로 전달, 1일 이후 자동 수집
        ShippingFee: selectedGoods.ShippingFee,
        GoodsRegisterDate: selectedGoods.GoodsRegisterDate,
        NoticeYn: selectedGoods.NoticeYn,
        IsMyStoreGoods: selectedGoods.IsMyStoreGoods,
        MallSeq: selectedGoods.MallSeq,
        InsertYn: 'N', // 기존 등록된 상품이므로 N
      },
      {
        onSuccess: () => {
          showToast('success', '키워드가 삭제되었어요. 반영까지 최대 하루가 소요될 수 있어요.');
          refetch();
        },
        onError: () => {
          showToast('warning', '키워드 삭제에 실패했습니다. 다시 시도해주세요.');
        },
      },
    );
  };

  return (
    <table className="min-w-[600px]  text-gr-800">
      <thead>
        <tr className="sticky top-0 z-[10] rounded-t-[8px] bg-gr-50 Caption1M12">
          <th className="sticky left-0 overflow-hidden text-ellipsis whitespace-nowrap border-b  border-r  bg-gr-50  px-3 py-2 text-left">
            키워드
          </th>
          {dateList.map((date) => (
            <th
              key={date}
              className="overflow-hidden text-ellipsis whitespace-nowrap border-b bg-gr-50 px-[10px]  py-2 text-left Caption1M12"
            >
              {formatDateWithDay(date)}
            </th>
          ))}
        </tr>
      </thead>

      <tbody>
        {keywordList.map((keyword: string) => (
          <tr key={keyword}>
            <td className="sticky left-0 min-w-[180px] overflow-hidden text-ellipsis whitespace-nowrap border-b border-r  bg-white px-3 py-2 text-gr-800 Body5M14 ">
              <div className="flex w-full items-center justify-between">
                {keyword}

                <button
                  type="button"
                  className="flex h-[34px] w-[34px] items-center justify-center rounded-[8px] border py-[8px] text-gr-400 hover:bg-white/90"
                  onClick={() => {
                    handleRemoveKeywords(keyword);
                  }}
                  aria-label="삭제"
                >
                  <TrashIcon />
                </button>
              </div>
            </td>
            {dailyList.map((day: MonitoringGoodsDetailDaily, idx: number) => {
              const { rank, status, range } = getKeywordRank(day, keyword);
              return (
                <td
                  key={idx}
                  className="min-w-[120px] overflow-hidden text-ellipsis whitespace-nowrap border-b px-[10px] py-2 text-left  text-gr-800  Body5M14"
                  style={{ maxWidth: 120 }}
                >
                  {rank}
                  {status === 'up' && <span className="ml-1">(▲{Math.abs(range)})</span>}
                  {status === 'down' && <span className="ml-1 ">(▼{Math.abs(range)})</span>}
                  {status === 'stay' && <span className="ml-1">(-)</span>}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default GoodsDetailKeywordTable;

// 요일 한글 배열
const WEEKDAY_KO = ['일', '월', '화', '수', '목', '금', '토'];

function formatDateWithDay(dateStr: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const weekday = WEEKDAY_KO[date.getDay()];
  return `${month}.${day}(${weekday})`;
}
